import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchOpenTabs, recordTabPayment, splitTabPayment, setTabDiscount, reopenTab,
  fetchClosedTabs, fetchZReport, getCurrency, getUser,
  hasFeature, fetchInhouseGuestsLite, chargeTabToRoom,
  fetchOpenBlocksLite, chargeTabToGroup,
  fetchTables, transferTabItems, fetchStaff,
  fetchServiceBookings, completeBooking, refreshMe,
  fetchBranding, resolveImageUrl,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";
import { serialSupported, isConnected, connectPrinter, printReceipt } from "../services/thermalPrinter";

// Cashier dashboard — read each open tab, key it into the existing POS /
// register, then record the bill (which closes the tab). Also: apply a
// discount, record tendered/change, print a proforma bill (NOT a fiscal
// receipt), reopen a mistaken bill, and see a day-close (Z-report) summary.
const STRINGS = {
  en: {
    title: "Cashier", sub: "Read each tab, enter it into your existing POS / register, then record the bill here.",
    new_order: "New order", to_bill: "To bill", billed_today: "Billed today", no_tabs: "No open tabs to bill.", no_billed: "Nothing billed today yet.",
    wellness: "Wellness", no_wellness: "No wellness bookings to collect.", collect: "Collect",
    table: "Table", waiter: "Waiter", subtotal: "Subtotal", discount: "Discount", service: "Service", vat: "VAT", total: "Total",
    pos_ref: "POS bill #", method: "Paid by", record_bill: "Record bill", refresh: "Refresh",
    split_bill: "Split bill", pay_selected: "Pay selected", paid: "Paid", remaining: "Remaining",
    select_to_split: "Select items to pay separately", selected: "Selected",
    transfer: "Transfer", transfer_to: "To table", transfer_selected: "Transfer selected", choose_table: "Choose a table",
    apply: "Apply", disc_ph: "Discount (birr)", disc_reason_ph: "Reason", pin_prompt: "Manager PIN to approve:", reopen_reason_prompt: "Reason for reopening this bill:", tendered: "Tendered", change: "Change", print: "Print bill",
    company_invoice: "Company invoice (TIN)", company_ph: "Company name", tin_ph: "TIN",
    salesperson: "Salesperson", salesperson_auto: "Auto (order taker)",
    reopen: "Reopen", reopen_confirm: "Reopen this bill? It will be un-billed so you can correct it.",
    day_close: "Day close (Z)", count: "Bills", note_fiscal: "ODRA total is the pre-tax subtotal — the fiscal receipt comes from your register.",
    proforma: "PROFORMA — not a fiscal receipt",
    efp_title: "E-invoice registered", efp_dryrun: "dry-run", efp_sub: "Scan to verify this fiscal receipt.", efp_close: "Close", print_invoice: "Print invoice",
    m_pos: "POS / register", m_cash: "Cash", m_telebirr: "Telebirr", m_cbe: "CBE Birr", m_other: "Other", m_room: "Room",
    charge_to_room: "Charge to room", pick_guest: "Choose in-house guest", search_room_or_name: "Search room # or guest name",
    charged_to_room: "Charged to room {room}", no_inhouse: "No in-house guests right now.",
    approval_requested: "Approval request sent to guest.",
    charge_confirm: "Send this bill to Room {room} · {guest}?  Amount: {amount}",
    charge_to_group: "Charge to group", pick_group: "Choose group", search_group: "Search group / company",
    charged_to_group: "Charged to group {group}", no_blocks: "No open groups right now.",
    group_confirm: "Send this bill to group {group}?  Amount: {amount}",
    v_title: "Charge to room", v_not_receipt: "Not a payment receipt", v_room: "Room", v_guest: "Guest",
    v_tab: "Tab", v_total: "Total charged to room", v_sign: "Guest signature",
    v_settle: "Charged to the room account — settle at checkout.",
    print_for_signature: "Print for signature",
  },
  am: {
    title: "ገንዘብ ተቀባይ", sub: "እያንዳንዱን ሰንጠረዥ አንብበው ወደ POS / መመዝገቢያዎ ያስገቡ፣ ከዚያ እዚህ ይመዝግቡ።",
    new_order: "አዲስ ትዕዛዝ", to_bill: "የሚከፈሉ", billed_today: "ዛሬ የተከፈሉ", no_tabs: "የሚከፈል ክፍት ሰንጠረዥ የለም።", no_billed: "ዛሬ የተከፈለ የለም።",
    wellness: "ዌልነስ", no_wellness: "የሚሰበሰብ የዌልነስ ማስያዣ የለም።", collect: "ሰብስብ",
    table: "ጠረጴዛ", waiter: "አስተናጋጅ", subtotal: "ንዑስ ድምር", discount: "ቅናሽ", service: "አገልግሎት", vat: "ተ.እ.ታ", total: "ጠቅላላ",
    pos_ref: "የPOS ደረሰኝ #", method: "የተከፈለበት", record_bill: "ክፍያ መዝግብ", refresh: "አድስ",
    split_bill: "ሒሳብ ክፈል", pay_selected: "የተመረጠውን ክፈል", paid: "ተከፍሏል", remaining: "ቀሪ",
    select_to_split: "ለየብቻ የሚከፈሉ ንጥሎችን ይምረጡ", selected: "የተመረጠ",
    transfer: "አዛውር", transfer_to: "ወደ ጠረጴዛ", transfer_selected: "የተመረጠውን አዛውር", choose_table: "ጠረጴዛ ይምረጡ",
    apply: "ተግብር", disc_ph: "ቅናሽ (ብር)", disc_reason_ph: "ምክንያት", pin_prompt: "ለማጽደቅ የሥራ አስኪያጅ ፒን:", reopen_reason_prompt: "ደረሰኙን እንደገና ለመክፈት ምክንያት:", tendered: "የተሰጠ", change: "መልስ", print: "ደረሰኝ አትም",
    company_invoice: "የድርጅት ደረሰኝ (ቲን)", company_ph: "የድርጅት ስም", tin_ph: "ቲን",
    salesperson: "ሻጭ", salesperson_auto: "በራስ (ትዕዛዝ ተቀባይ)",
    reopen: "እንደገና ክፈት", reopen_confirm: "ይህን ደረሰኝ እንደገና ይክፈቱ? ለማስተካከል ይመለሳል።",
    day_close: "የቀን ዝጋ (Z)", count: "ደረሰኞች", note_fiscal: "የODRA ድምር ከግብር በፊት ነው — ኦፊሴላዊ ደረሰኝ ከመመዝገቢያዎ ይወጣል።",
    proforma: "ፕሮፎርማ — ኦፊሴላዊ ደረሰኝ አይደለም",
    efp_title: "ኢ-ደረሰኝ ተመዝግቧል", efp_dryrun: "ሙከራ", efp_sub: "ይህን ኦፊሴላዊ ደረሰኝ ለማረጋገጥ ይቃኙ።", efp_close: "ዝጋ", print_invoice: "ደረሰኝ አትም",
    m_pos: "POS / መመዝገቢያ", m_cash: "ጥሬ ገንዘብ", m_telebirr: "ቴሌብር", m_cbe: "ሲቢኢ ብር", m_other: "ሌላ", m_room: "ክፍል",
    charge_to_room: "ወደ ክፍል አስከፍል", pick_guest: "የቤት ውስጥ እንግዳ ይምረጡ", search_room_or_name: "የክፍል # ወይም የእንግዳ ስም ይፈልጉ",
    charged_to_room: "ወደ ክፍል {room} ተከፍሏል", no_inhouse: "አሁን የቤት ውስጥ እንግዳ የለም።",
    approval_requested: "የማጽደቅ ጥያቄ ለእንግዳው ተልኳል።",
    charge_confirm: "ይህን ሂሳብ ወደ ክፍል {room} · {guest} ይላኩ?  መጠን: {amount}",
    charge_to_group: "ወደ ቡድን አስከፍል", pick_group: "ቡድን ይምረጡ", search_group: "ቡድን / ድርጅት ይፈልጉ",
    charged_to_group: "ወደ ቡድን {group} ተከፍሏል", no_blocks: "አሁን ክፍት ቡድን የለም።",
    group_confirm: "ይህን ሂሳብ ወደ ቡድን {group} ይላኩ?  መጠን: {amount}",
    v_title: "ወደ ክፍል አስከፍል", v_not_receipt: "የክፍያ ደረሰኝ አይደለም", v_room: "ክፍል", v_guest: "እንግዳ",
    v_tab: "ትዕዛዝ", v_total: "ወደ ክፍል የተከፈለ ጠቅላላ", v_sign: "የእንግዳ ፊርማ",
    v_settle: "ወደ ክፍል ሂሳብ ተከፍሏል — ሲወጡ ያወራርዱ።",
    print_for_signature: "ለፊርማ አትም",
  },
  om: {
    title: "Qaanqee", sub: "Gabatee tokkoon tokkoon dubbisii POS / galmee kee keessa galchi, achii as galmeessi.",
    new_order: "Ajaja haaraa", to_bill: "Kaffalaman", billed_today: "Har'a kaffalame", no_tabs: "Gabatee banaa kaffalamu hin jiru.", no_billed: "Har'a wanti kaffalame hin jiru.",
    wellness: "Wellness", no_wellness: "Qabannoo wellness sassaabamu hin jiru.", collect: "Sassaabi",
    table: "Gabatee", waiter: "Tajaajilaa", subtotal: "Ida'ama xiqqaa", discount: "Hir'isa", service: "Tajaajila", vat: "VAT", total: "Ida'ama",
    pos_ref: "Lakk. POS", method: "Kan kaffalame", record_bill: "Galmeessi", refresh: "Haaromsi",
    split_bill: "Bill qoodi", pay_selected: "Filatame kaffali", paid: "Kaffalame", remaining: "Hafe",
    select_to_split: "Wantoota adda kaffalaman filadhu", selected: "Filatame",
    transfer: "Dabarsi", transfer_to: "Gara minjaalaa", transfer_selected: "Filatame dabarsi", choose_table: "Minjaala filadhu",
    apply: "Hojiirra oolchi", disc_ph: "Hir'isa (birr)", disc_reason_ph: "Sababa", pin_prompt: "Mirkaneessuuf PIN maanaajeraa:", reopen_reason_prompt: "Nagahee kana deebisanii banuuf sababa:", tendered: "Kan kenname", change: "Deebii", print: "Nagahee maxxansi",
    company_invoice: "Nagahee dhaabbataa (TIN)", company_ph: "Maqaa dhaabbataa", tin_ph: "TIN",
    salesperson: "Gurgurtaa", salesperson_auto: "Ofumaan (nama ajaja fudhate)",
    reopen: "Irra deebi'ii bani", reopen_confirm: "Nagahee kana irra deebitee banta? Sirreessuf ni deebi'a.",
    day_close: "Cufaa guyyaa (Z)", count: "Nagahee", note_fiscal: "Ida'amni ODRA gibira dura — nagaheen seeraa galmee kee irraa ba'a.",
    proforma: "PROFORMA — nagahee seeraa miti",
    efp_title: "E-invoice galmaa'e", efp_dryrun: "yaalii", efp_sub: "Nagahee seeraa kana mirkaneessuuf skaani godhi.", efp_close: "Cufi", print_invoice: "Nagahee maxxansi",
    m_pos: "POS / galmee", m_cash: "Maallaqa", m_telebirr: "Telebirr", m_cbe: "CBE Birr", m_other: "Kan biraa", m_room: "Kutaa",
    charge_to_room: "Kutaatti kaffalchiisi", pick_guest: "Keessummaa mana keessaa fili", search_room_or_name: "Lakk. kutaa yookaan maqaa keessummaa barbaadi",
    charged_to_room: "Kutaa {room}tti kaffalame", no_inhouse: "Yeroo ammaa keessummaan mana keessaa hin jiru.",
    approval_requested: "Gaaffiin mirkaneessaa keessummaadhaaf ergameera.",
    charge_confirm: "Herrega kana gara Kutaa {room} · {guest} ergi?  Hanga: {amount}",
    charge_to_group: "Gareetti kaffalchiisi", pick_group: "Garee fili", search_group: "Garee / dhaabbata barbaadi",
    charged_to_group: "Gareetti {group} kaffalame", no_blocks: "Yeroo ammaa gareen banaa hin jiru.",
    group_confirm: "Herrega kana gara garee {group} ergi?  Hanga: {amount}",
    v_title: "Kutaatti kaffalchiisi", v_not_receipt: "Nagahee kaffaltii miti", v_room: "Kutaa", v_guest: "Keessummaa",
    v_tab: "Ajaja", v_total: "Ida'ama kutaatti kaffalame", v_sign: "Mallattoo keessummaa",
    v_settle: "Herrega kutaatti kaffalame — yeroo ba'an kaffali.",
    print_for_signature: "Mallattoof maxxansi",
  },
};

export default function CashierPage() {
  const { lang } = useT();
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;
  const cur = getCurrency() || "ETB";
  const money = (v) => formatPrice(v || 0, cur);

  const [view, setView] = useState("tobill");
  const [tabs, setTabs] = useState([]);
  const [closed, setClosed] = useState([]);
  const [z, setZ] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(null);
  const [refs, setRefs] = useState({});
  const [methods, setMethods] = useState({});
  const [tend, setTend] = useState({});
  const [disc, setDisc] = useState({});
  const [discReason, setDiscReason] = useState({});
  // Company invoice (optional) — buyer company + TIN for a compliant bill.
  const [tinOpen, setTinOpen] = useState({});   // tab.id -> panel open?
  const [company, setCompany] = useState({});   // tab.id -> company name
  const [tin, setTin] = useState({});           // tab.id -> TIN
  const [staff, setStaff] = useState([]);       // staff list for the salesperson picker
  const [salesperson, setSalesperson] = useState({});  // tab.id -> salesperson_staff_id override
  const [flash, setFlash] = useState("");
  // Split bill — per-tab panel open flag + set of selected item ids.
  const [splitOpen, setSplitOpen] = useState({});  // tab.id -> bool
  const [splitSel, setSplitSel] = useState({});    // tab.id -> array of item ids
  // Bill transfer — relocate selected unsettled items to another table's tab.
  const [tables, setTables] = useState([]);            // all tables (target picker)
  const [transferOpen, setTransferOpen] = useState({});   // tab.id -> bool
  const [transferSel, setTransferSel] = useState({});     // tab.id -> array of item ids
  const [transferTarget, setTransferTarget] = useState({}); // tab.id -> target table_id
  // Charge to room (in-house F&B billing) — only for hotels with the front desk.
  const fdOn = hasFeature("front_desk");
  const [inhouse, setInhouse] = useState(null);   // null = not loaded yet
  const [roomPick, setRoomPick] = useState({});   // tab.id -> panel open?
  const [gQuery, setGQuery] = useState({});        // tab.id -> guest search text
  const [blocks, setBlocks] = useState(null);      // null = not loaded yet
  const [groupPick, setGroupPick] = useState({});  // tab.id -> group panel open?
  const [bQuery, setBQuery] = useState({});        // tab.id -> group search text
  const [signSlip, setSignSlip] = useState(null);  // {tab, guest} of the last room charge, for an optional signature slip
  // Thermal printer (Web Serial, no-install path). Off until the cashier
  // connects a printer; everything falls back to browser print otherwise.
  const [printerOn, setPrinterOn] = useState(false);
  const [settledFiscal, setSettledFiscal] = useState(null);  // {irn, provider, qr_image} shown after a bill is cleared
  // Hotel's own logo for the fiscal invoice header (falls back to the ODRA mark).
  const [venueLogo, setVenueLogo] = useState(null);
  useEffect(() => { fetchBranding().then((b) => setVenueLogo(resolveImageUrl(b && b.logo_url))).catch(() => {}); }, []);
  // Wellness — open (unpaid) walk-in spa/gym bookings the cashier can settle here
  // as well as at the spa desk. Each carries its own chosen payment method.
  const hasWellness = hasFeature("services");
  const [wellness, setWellness] = useState([]);
  const [wMethod, setWMethod] = useState({});   // booking.id -> payment method
  // Per-hotel policy: whether charging a tab to a group / transferring bills is allowed.
  const [allowGroupCharge, setAllowGroupCharge] = useState(true);
  const connectThermal = async () => {
    try { await connectPrinter(); setPrinterOn(true); setErr(""); }
    catch (e) { setErr(e?.message || "Could not connect the printer."); }
  };

  const load = useCallback(() => {
    fetchOpenTabs().then((r) => setTabs((r || []).filter((tb) => (tb.round_count || 0) > 0))).catch((e) => setErr(e?.response?.data?.detail || "Could not load."));
    fetchClosedTabs().then(setClosed).catch(() => {});
    fetchZReport().then(setZ).catch(() => {});
    if (hasWellness) {
      // Open walk-ins to collect: pay-direct bookings not yet settled.
      fetchServiceBookings()
        .then((r) => setWellness((r || []).filter((b) => b.billing_kind === "paid" && !b.payment_method && b.status !== "cancelled" && b.status !== "no_show")))
        .catch(() => setWellness([]));
    }
  }, [hasWellness]);

  useEffect(() => { load(); const id = setInterval(load, 25000); return () => clearInterval(id); }, [load]);
  // Tables for the transfer target picker — loaded once.
  useEffect(() => { fetchTables().then((r) => setTables(r || [])).catch(() => setTables([])); }, []);
  // Staff list for the salesperson (seller) picker — blank means "use the order-taker".
  useEffect(() => { fetchStaff().then((r) => setStaff((r || []).filter((s) => s.active !== false))).catch(() => setStaff([])); }, []);
  // Per-hotel billing policy (group charge / bill transfer on or off).
  useEffect(() => { refreshMe().then((me) => setAllowGroupCharge(me.allow_group_charge !== false)).catch(() => {}); }, []);

  // Settle an open walk-in wellness booking here at the cashier: post the sale
  // via complete_booking with the chosen method, then drop it from the list.
  const collectWellness = async (b) => {
    setBusy("w" + b.id); setErr("");
    try {
      await completeBooking(b.id, { payment_method: wMethod[b.id] || "cash" });
      setWellness((ws) => ws.filter((x) => x.id !== b.id));
      setFlash(`${b.service_name || t("service")} · ${money(b.price)}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not collect.");
    } finally { setBusy(null); }
  };

  const recordBill = async (tab) => {
    setBusy(tab.id); setErr("");
    try {
      const res = await recordTabPayment(tab.id, {
        method: methods[tab.id] || "pos",
        reference: (refs[tab.id] || "").trim() || null,
        amount: (tab.balance && tab.balance.total != null) ? tab.balance.total : tab.total,
        tendered: tend[tab.id] ? Number(tend[tab.id]) : null,
        buyer_company: (company[tab.id] || "").trim() || null,
        buyer_tin: (tin[tab.id] || "").trim() || null,
        salesperson_staff_id: salesperson[tab.id] ? Number(salesperson[tab.id]) : null,
      });
      const fiscal = (res && res.fiscal && res.fiscal.irn) ? res.fiscal : null;
      if (fiscal) setSettledFiscal({ ...fiscal, tab });   // show e-invoice at the point of sale
      const receipt = receiptFromTab(tab, fiscal);   // capture before clearing inputs
      setRefs((r) => { const n = { ...r }; delete n[tab.id]; return n; });
      setTend((r) => { const n = { ...r }; delete n[tab.id]; return n; });
      setCompany((r) => { const n = { ...r }; delete n[tab.id]; return n; });
      setTin((r) => { const n = { ...r }; delete n[tab.id]; return n; });
      setSalesperson((r) => { const n = { ...r }; delete n[tab.id]; return n; });
      load();
      // If a thermal printer is connected, print the bill and kick the drawer
      // automatically. Otherwise the cashier can still print from the button.
      if (printerOn && isConnected()) { printReceipt(receipt, { cur, openDrawer: (methods[tab.id] || "pos") === "cash" }); }
    } catch (e) { setErr(e?.response?.data?.detail || "Could not record the bill."); }
    finally { setBusy(null); }
  };

  // Split bill — toggle the panel / an item's selection for a tab.
  const toggleSplit = (tab) =>
    setSplitOpen((m) => ({ ...m, [tab.id]: !m[tab.id] }));
  const toggleSplitItem = (tabId, itemId) =>
    setSplitSel((m) => {
      const cur = m[tabId] || [];
      const next = cur.includes(itemId) ? cur.filter((x) => x !== itemId) : [...cur, itemId];
      return { ...m, [tabId]: next };
    });
  const paySelected = async (tab) => {
    const idxs = splitSel[tab.id] || [];
    if (!idxs.length) return;
    const its = (tab.rounds || []).flatMap((r) => r.items || []);
    const item_ids = idxs.map((i) => its[i] && its[i].id).filter((x) => x != null);
    if (item_ids.length !== idxs.length) { setErr("Items are missing their id — update the server (menulink-api) to the latest version."); return; }
    setBusy(tab.id); setErr("");
    try {
      await splitTabPayment(tab.id, {
        item_ids,
        method: methods[tab.id] || "pos",
        reference: (refs[tab.id] || "").trim() || null,
        tendered: tend[tab.id] ? Number(tend[tab.id]) : null,
        buyer_company: (company[tab.id] || "").trim() || null,
        buyer_tin: (tin[tab.id] || "").trim() || null,
        salesperson_staff_id: salesperson[tab.id] ? Number(salesperson[tab.id]) : null,
      });
      setSplitSel((m) => { const n = { ...m }; delete n[tab.id]; return n; });
      load();
      // The tab either loses its settled items (panel stays open on the
      // refreshed card) or drops out of the open-tabs list once fully paid.
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not split the bill.");
    } finally { setBusy(null); }
  };

  // Bill transfer — toggle the panel / an item's selection for a tab.
  const toggleTransfer = (tab) =>
    setTransferOpen((m) => ({ ...m, [tab.id]: !m[tab.id] }));
  const toggleTransferItem = (tabId, itemId) =>
    setTransferSel((m) => {
      const cur = m[tabId] || [];
      const next = cur.includes(itemId) ? cur.filter((x) => x !== itemId) : [...cur, itemId];
      return { ...m, [tabId]: next };
    });
  const doTransfer = async (tab) => {
    const idxs = transferSel[tab.id] || [];
    const target = transferTarget[tab.id];
    if (!idxs.length || !target) return;
    const its = (tab.rounds || []).flatMap((r) => r.items || []);
    const item_ids = idxs.map((i) => its[i] && its[i].id).filter((x) => x != null);
    if (item_ids.length !== idxs.length) { setErr("Items are missing their id — update the server (menulink-api) to the latest version."); return; }
    setBusy(tab.id); setErr("");
    try {
      await transferTabItems(tab.id, {
        item_ids,
        target_table_id: Number(target),
      });
      setTransferSel((m) => { const n = { ...m }; delete n[tab.id]; return n; });
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr((d && typeof d === "object" ? d.message : d) || "Could not transfer the items.");
    } finally { setBusy(null); }
  };

  const errDetail = (e) => {
    const d = e?.response?.data?.detail;
    return (d && typeof d === "object") ? d : { message: d };
  };
  const applyDiscount = async (tab, pin) => {
    const amount = Number(disc[tab.id]) || 0;
    const reason = (discReason[tab.id] || "").trim();
    try {
      await setTabDiscount(tab.id, { amount, reason, manager_pin: pin });
      setErr(""); load();
    } catch (e) {
      const d = errDetail(e);
      if (d.code === "pin_required") {
        const entered = window.prompt(t("pin_prompt"));
        if (entered) return applyDiscount(tab, entered);
        return;
      }
      setErr(d.message || "Could not apply discount.");
    }
  };
  const doReopen = async (tab, opts) => {
    opts = opts || {};
    let reason = opts.reason;
    if (reason == null) {
      reason = window.prompt(t("reopen_reason_prompt"));
      if (!reason || !reason.trim()) return;
      reason = reason.trim();
    }
    setBusy(tab.id);
    try {
      await reopenTab(tab.id, { reason, manager_pin: opts.pin });
      setErr(""); load();
    } catch (e) {
      const d = errDetail(e);
      if (d.code === "pin_required") {
        const entered = window.prompt(t("pin_prompt"));
        if (entered) { setBusy(null); return doReopen(tab, { reason, pin: entered }); }
      } else {
        setErr(d.message || "Could not reopen.");
      }
    } finally { setBusy(null); }
  };

  const openRoomPick = (tab) => {
    setRoomPick((m) => ({ ...m, [tab.id]: !m[tab.id] }));
    if (inhouse == null) fetchInhouseGuestsLite().then(setInhouse).catch(() => setInhouse([]));
  };
  const filterGuests = (q) => {
    const s = (q || "").trim().toLowerCase();
    return (inhouse || []).filter((g) => !s
      || (g.room_name || "").toLowerCase().includes(s)
      || (g.guest_name || "").toLowerCase().includes(s));
  };
  const doChargeRoom = async (tab, guest) => {
    // Confirm before posting to the room folio. Vouchers now print at the
    // front desk (reception), NOT here — the cashier only sends the charge.
    const msg = t("charge_confirm")
      .replace("{room}", guest.room_name || "—")
      .replace("{guest}", guest.guest_name || "—")
      .replace("{amount}", money(tab.total));
    if (!window.confirm(msg)) return;
    setBusy(tab.id); setErr(""); setFlash("");
    try {
      const res = await chargeTabToRoom(tab.id, guest.id);
      setRoomPick((m) => ({ ...m, [tab.id]: false }));
      setFlash(t("charged_to_room").replace("{room}", guest.room_name || guest.guest_name)
        + (res && res.approval_sent ? " " + t("approval_requested") : ""));
      // Offer an OPTIONAL signature slip (no auto-print): staff can hand the
      // guest a slip to sign at the outlet.
      setSignSlip({ tab, guest });
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr((d && typeof d === "object" ? d.message : d) || "Could not charge to room.");
    } finally { setBusy(null); }
  };

  const openGroupPick = (tab) => {
    setGroupPick((m) => ({ ...m, [tab.id]: !m[tab.id] }));
    if (blocks == null) fetchOpenBlocksLite().then(setBlocks).catch(() => setBlocks([]));
  };
  const filterBlocks = (q) => {
    const s = (q || "").trim().toLowerCase();
    return (blocks || []).filter((b) => !s || (b.label || "").toLowerCase().includes(s));
  };
  const doChargeGroup = async (tab, blk) => {
    // Confirm before posting to the group master bill. Group charges print on
    // the group statement at the front desk — no per-charge voucher here.
    const msg = t("group_confirm")
      .replace("{group}", blk.label || "—")
      .replace("{amount}", money(tab.total));
    if (!window.confirm(msg)) return;
    setBusy(tab.id); setErr(""); setFlash("");
    try {
      await chargeTabToGroup(tab.id, blk.id);
      setGroupPick((m) => ({ ...m, [tab.id]: false }));
      setFlash(t("charged_to_group").replace("{group}", blk.label || ""));
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr((d && typeof d === "object" ? d.message : d) || "Could not charge to group.");
    } finally { setBusy(null); }
  };

  // Print the ODRA fiscal-style A4 invoice (MoR/DROGA bilingual layout). When a
  // fiscal result (IRN + QR) is available — e.g. straight after settlement —
  // it prints as a registered Sales Invoice; otherwise it prints the same
  // layout marked "Proforma — not a fiscal receipt".
  const printBill = (tab, fiscal = null) => {
    const w = window.open("", "_blank", "width=820,height=1000");
    if (!w) return;
    w.document.write(fiscalBillHtml(tab, { user: getUser(), fiscal, cur, money, proformaLabel: t("proforma"), venueLogo }));
    w.document.close(); w.focus(); setTimeout(() => w.print(), 450);
  };

  // Room-charge voucher — a CONTROL document the guest signs and the outlet
  // keeps. NOT a payment receipt. Browser print (window.open), thermal width,
  // mirroring the proforma bill styling above. Amount is authoritative from the
  // charge-to-room response (net posted to the folio). Kept as the shared
  // reference markup — vouchers now print at the front desk (ReservationsPage);
  // the cashier may also print an optional signature slip via the button below.
  const printRoomVoucher = (tab, guest, res) => {
    const items = (tab.rounds || []).flatMap((r) => r.items || []);
    const rows = items.map((it) => `<tr><td>${it.quantity}× ${escapeHtml(it.name)}</td><td class="r">${money(it.line_total)}</td></tr>`).join("");
    const hotel = res.hotel || (getUser() || {}).name || "";
    const room = res.room_name || guest.room_name || "";
    const gname = res.guest_name || guest.guest_name || "";
    const amount = res.amount != null ? res.amount : tab.subtotal;
    const tableLbl = tab.table_number != null ? ` · ${t("table")} ${tab.table_number}` : "";
    const w = window.open("", "_blank", "width=380,height=640");
    if (!w) return;
    w.document.write(`<html><head><meta charset="utf-8"><title>${escapeHtml(hotel)} — ${escapeHtml(t("v_title"))}</title><style>
      body{font-family:Arial,Helvetica,sans-serif;max-width:360px;margin:0 auto;padding:16px;color:#111}
      h2{margin:0;font-size:17px}.muted{color:#666;font-size:11.5px}
      .head{text-align:center}
      .title{text-align:center;font-weight:700;font-size:15px;letter-spacing:.6px;text-transform:uppercase;margin:8px 0 0}
      .sub{text-align:center;font-size:11px;color:#666;margin-bottom:2px}
      table{width:100%;border-collapse:collapse;margin-top:6px;font-size:12.5px}td{padding:3px 0;vertical-align:top}
      .r{text-align:right}.tot td{border-top:1px solid #999;font-weight:700;padding-top:6px}
      .hr{border-top:1px dashed #999;margin:8px 0}
      .sign{margin-top:22px;font-size:12.5px}
      .note{margin-top:14px;font-size:11px;text-align:center;color:#555}</style></head><body>
      <div class="head"><h2>${escapeHtml(hotel)}</h2></div>
      <div class="title">${escapeHtml(t("v_title"))}</div>
      <div class="sub">${escapeHtml(t("v_not_receipt"))}</div>
      <div class="muted head">${new Date().toLocaleString()}</div>
      <div class="hr"></div>
      <div class="muted"><b>${t("v_room")}:</b> ${escapeHtml(room || "—")}</div>
      <div class="muted"><b>${t("v_guest")}:</b> ${escapeHtml(gname || "—")}</div>
      <div class="muted">${t("v_tab")} #${tab.id}${tableLbl}</div>
      <div class="hr"></div>
      ${rows ? `<table>${rows}</table>` : ""}
      <table><tr class="tot"><td>${t("v_total")}</td><td class="r">${money(amount)}</td></tr></table>
      <div class="sign">${t("v_sign")}: ______________________</div>
      <div class="note">${escapeHtml(t("v_settle"))}</div>
    </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 250);
  };

  // Build a receipt object (for the thermal encoder) from a tab. When `fiscal`
  // (IRN + QR from the connector) is supplied the slip prints as a registered
  // fiscal invoice; otherwise it prints the proforma notice.
  const receiptFromTab = (tab, fiscal = null) => {
    const u = getUser() || {};
    const fiscalised = !!(fiscal && fiscal.irn);
    return {
      hotel: {
        name: u.name || "",
        address: [u.sub_city, u.woreda ? "Woreda " + u.woreda : "", u.city].filter(Boolean).join(", "),
        phone: u.phone || "",
        tin: u.tin || u.vat_number ? (u.tin || "") : "",
        vat_reg_no: u.vat_number || "",
      },
      doc_title: fiscalised ? "SALES INVOICE / VAT" : t("proforma"),
      invoice_no: (fiscal && fiscal.invoice_no) || (refs[tab.id] || "").trim() || null,
      datetime: new Date().toLocaleString(),
      lines: (tab.rounds || []).flatMap((r) => r.items || []).map((it) => ({ description: it.name, qty: it.quantity, amount: it.line_total })),
      subtotal_net: tab.subtotal,
      discount: tab.discount,
      tax_label: tab.vat > 0 ? `${t("vat")} ${tab.vat_pct}%` : "",
      tax_amount: tab.vat,
      total: tab.total,
      method: methods[tab.id],
      tendered: tend[tab.id] ? Number(tend[tab.id]) : null,
      change: tend[tab.id] ? Number(tend[tab.id]) - (tab.total || 0) : null,
      reference: (refs[tab.id] || "").trim() || null,
      footer: fiscalised ? "Fiscalized by KODIS e-Invoice - odra.app" : t("proforma"),
      fiscal: fiscalised ? {
        irn: fiscal.irn, rrn: fiscal.rrn,
        system_number: fiscal.system_number, qr_data: fiscal.qr_data, provider: fiscal.provider,
      } : null,
    };
  };

  // Print via the thermal printer if one is connected, else fall back to the
  // browser print dialog (the existing behaviour).
  const smartPrint = async (tab) => {
    if (printerOn && isConnected()) {
      const ok = await printReceipt(receiptFromTab(tab), { cur, openDrawer: (methods[tab.id] || "pos") === "cash" });
      if (ok) return;
    }
    printBill(tab);
  };

  const totalToBill = tabs.reduce((a, tb) => a + (tb.total || 0), 0);
  const methodSel = (tab) => (
    <select value={methods[tab.id] || "pos"} onChange={(e) => setMethods((m) => ({ ...m, [tab.id]: e.target.value }))}
      style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #d6dede" }}>
      <option value="pos">{t("m_pos")}</option><option value="cash">{t("m_cash")}</option>
      <option value="telebirr">{t("m_telebirr")}</option><option value="cbe_birr">{t("m_cbe")}</option>
      <option value="other">{t("m_other")}</option>
    </select>
  );
  const inp = { padding: "8px 10px", borderRadius: 8, border: "1px solid #d6dede" };
  const Row = ({ label, value, strong }) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
      <span className="muted">{label}</span><span style={strong ? { fontWeight: 700, color: "#008080" } : {}}>{value}</span>
    </div>
  );

  return (
    <div className="page">
      {settledFiscal && (
        <div onClick={() => setSettledFiscal(null)}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 60,
                      display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: "#fff", borderRadius: 12, padding: 20, maxWidth: 340, width: "100%",
                        textAlign: "center", boxShadow: "0 10px 40px rgba(0,0,0,.25)" }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2 }}>
              {t("efp_title")}{settledFiscal.provider === "mock" ? ` (${t("efp_dryrun")})` : ""}
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>{t("efp_sub")}</div>
            {settledFiscal.qr_image
              ? <img src={settledFiscal.qr_image} alt="fiscal QR"
                     style={{ width: 180, height: 180, imageRendering: "pixelated" }} />
              : <div className="muted" style={{ padding: 24 }}>—</div>}
            <div style={{ marginTop: 10, fontFamily: "monospace", fontSize: 13, wordBreak: "break-all" }}>
              {settledFiscal.irn}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              {settledFiscal.tab && (
                <button className="btn secondary" style={{ flex: 1 }}
                        onClick={() => printBill(settledFiscal.tab, settledFiscal)}>
                  🖨 {t("print_invoice")}
                </button>
              )}
              <button className="btn" style={{ flex: 1 }} onClick={() => setSettledFiscal(null)}>
                {t("efp_close")}
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ marginBottom: 4 }}>💵 {t("title")}</h1>
          <p className="muted" style={{ marginTop: 0, maxWidth: 640 }}>{t("sub")}</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Link className="btn" to="/take-order" style={{ textDecoration: "none" }}>＋ {t("new_order")}</Link>
          {serialSupported() && (
            <button className="btn secondary" onClick={connectThermal} title="Connect a USB thermal printer (Chrome)">
              🖨 {printerOn ? "Printer connected" : "Connect printer"}
            </button>
          )}
          <button className="btn secondary" onClick={load}>↻ {t("refresh")}</button>
        </div>
      </div>

      {/* Day-close (Z) summary */}
      {z && (
        <div className="card" style={{ marginTop: 10, padding: "10px 14px", display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center" }}>
          <b>{t("day_close")}</b>
          <span className="muted">{t("count")}: <b style={{ color: "#063b48" }}>{z.count}</b></span>
          <span className="muted">{t("total")}: <b style={{ color: "#008080" }}>{money(z.total)}</b></span>
          {z.discount > 0 && <span className="muted">{t("discount")}: {money(z.discount)}</span>}
          {Object.entries(z.by_method || {}).map(([m, v]) => (
            <span key={m} className="muted" style={{ fontSize: 12 }}>{t("m_" + (m === "cbe_birr" ? "cbe" : m)) || m}: {money(v)}</span>
          ))}
        </div>
      )}

      {/* View toggle */}
      <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
        {["tobill", ...(hasWellness ? ["wellness"] : []), "billed"].map((v) => (
          <button key={v} onClick={() => setView(v)}
            style={{ padding: "6px 14px", borderRadius: 16, border: "1px solid #d6dede", cursor: "pointer",
              background: view === v ? "#063b48" : "#fff", color: view === v ? "#fff" : "#333", fontSize: 13 }}>
            {v === "tobill" ? `${t("to_bill")} (${tabs.length})`
              : v === "wellness" ? `${t("wellness")} (${wellness.length})`
              : `${t("billed_today")} (${closed.length})`}
          </button>
        ))}
      </div>

      {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
      {flash && (
        <div className="card" style={{ marginTop: 8, padding: "8px 12px", background: "#e8f8e9", color: "#1b6b1e", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span>✓ {flash}</span>
          <span style={{ display: "flex", gap: 8 }}>
            {signSlip && (
              <button className="btn secondary" style={{ padding: "2px 10px" }}
                onClick={() => printRoomVoucher(signSlip.tab, signSlip.guest,
                  { amount: signSlip.tab.total, room_name: signSlip.guest.room_name, guest_name: signSlip.guest.guest_name })}>
                🖨 {t("print_for_signature")}
              </button>
            )}
            <button className="btn secondary" style={{ padding: "2px 10px" }} onClick={() => { setFlash(""); setSignSlip(null); }}>×</button>
          </span>
        </div>
      )}

      {view === "wellness" ? (
        wellness.length === 0 ? (
          <div className="card" style={{ marginTop: 12, padding: 24, textAlign: "center" }}><p className="muted" style={{ margin: 0 }}>{t("no_wellness")}</p></div>
        ) : (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {wellness.map((b) => (
              <div key={b.id} className="card" style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
                  <b>{b.service_name || t("service")}</b>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {b.customer_name || "—"}
                    {b.company_name ? ` · ${b.company_name}` : ""}
                    {b.scheduled_start ? ` · ${new Date(b.scheduled_start).toLocaleString()}` : ""}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <b>{money(b.price)}</b>
                  <select value={wMethod[b.id] || "cash"} onChange={(e) => setWMethod((m) => ({ ...m, [b.id]: e.target.value }))}
                    style={{ padding: "6px 8px", borderRadius: 8, border: "1px solid #d6dede", fontSize: 13 }}>
                    <option value="cash">{t("m_cash")}</option>
                    <option value="telebirr">{t("m_telebirr")}</option>
                    <option value="cbe_birr">{t("m_cbe")}</option>
                    <option value="pos">{t("m_pos")}</option>
                  </select>
                  <button className="btn primary" disabled={busy === "w" + b.id} onClick={() => collectWellness(b)}>{t("collect")}</button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : view === "tobill" ? (
        tabs.length === 0 ? (
          <div className="card" style={{ marginTop: 12, padding: 24, textAlign: "center" }}><p className="muted" style={{ margin: 0 }}>{t("no_tabs")}</p></div>
        ) : (
          <>
            <p className="muted" style={{ marginTop: 8 }}><b>{tabs.length}</b> · <b>{money(totalToBill)}</b></p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))", gap: 14 }}>
              {tabs.map((tab) => {
                const items = (tab.rounds || []).flatMap((r) => r.items || []);
                const waiterName = (tab.rounds || []).map((r) => r.waiter_name || r.placed_by_name).find(Boolean);
                const change = tend[tab.id] ? Number(tend[tab.id]) - (tab.total || 0) : null;
                return (
                  <div key={tab.id} className="card" style={{ padding: 16, display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <h3 style={{ margin: 0, color: "#063b48" }}>{t("table")} {tab.table_number ?? "—"}</h3>
                      {waiterName && <span className="muted" style={{ fontSize: 12 }}>{t("waiter")}: {waiterName}</span>}
                    </div>
                    <div style={{ marginTop: 10, borderTop: "1px solid #eef2f2", paddingTop: 8 }}>
                      {items.map((it, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 14, padding: "3px 0" }}>
                          <span><b>{it.quantity}×</b> {it.name}{it.code != null && <span className="muted" style={{ fontSize: 11 }}> #{it.code}</span>}</span>
                          <span>{money(it.line_total)}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 8, borderTop: "1px solid #eef2f2", paddingTop: 8, fontSize: 13 }}>
                      <Row label={t("subtotal")} value={money(tab.subtotal)} />
                      {tab.discount > 0 && <Row label={t("discount")} value={"−" + money(tab.discount)} />}
                      {tab.service_charge > 0 && <Row label={`${t("service")} (${tab.service_charge_pct}%)`} value={money(tab.service_charge)} />}
                      {tab.vat > 0 && <Row label={`${t("vat")} (${tab.vat_pct}%)`} value={money(tab.vat)} />}
                      <Row label={t("total")} value={money(tab.total)} strong />
                      {tab.balance && !tab.fully_paid && items.some((it) => it.settled) && (
                        <Row label={t("remaining")} value={money(tab.balance.total)} strong />
                      )}
                    </div>

                    <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
                      <input placeholder={t("disc_ph")} type="number" min="0" step="any" value={disc[tab.id] ?? ""}
                        onChange={(e) => setDisc((d) => ({ ...d, [tab.id]: e.target.value }))} style={{ ...inp, width: 110 }} />
                      <input placeholder={t("disc_reason_ph")} value={discReason[tab.id] || ""}
                        onChange={(e) => setDiscReason((r) => ({ ...r, [tab.id]: e.target.value }))} style={{ ...inp, flex: 1, minWidth: 90 }} />
                      <button className="btn secondary" onClick={() => applyDiscount(tab)}>{t("apply")}</button>
                      <button className="btn secondary" style={{ marginLeft: "auto" }} onClick={() => smartPrint(tab)}>🖨 {t("print")}</button>
                    </div>

                    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                      <input placeholder={t("pos_ref")} value={refs[tab.id] || ""} onChange={(e) => setRefs((r) => ({ ...r, [tab.id]: e.target.value }))} style={inp} />
                      <div style={{ display: "flex", gap: 6 }}>
                        <input placeholder={t("tendered")} type="number" min="0" step="any" value={tend[tab.id] || ""}
                          onChange={(e) => setTend((r) => ({ ...r, [tab.id]: e.target.value }))} style={{ ...inp, width: 100 }} />
                        {change != null && change >= 0 && <span style={{ alignSelf: "center", fontSize: 13 }} className="muted">{t("change")}: <b>{money(change)}</b></span>}
                      </div>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#5b6472", cursor: "pointer" }}>
                        <input type="checkbox" checked={!!tinOpen[tab.id]}
                          onChange={(e) => setTinOpen((m) => ({ ...m, [tab.id]: e.target.checked }))} />
                        {t("company_invoice")}
                      </label>
                      {tinOpen[tab.id] && (
                        <div style={{ display: "flex", gap: 6 }}>
                          <input placeholder={t("company_ph")} value={company[tab.id] || ""}
                            onChange={(e) => setCompany((m) => ({ ...m, [tab.id]: e.target.value }))} style={{ ...inp, flex: 1, minWidth: 90 }} />
                          <input placeholder={t("tin_ph")} value={tin[tab.id] || ""}
                            onChange={(e) => setTin((m) => ({ ...m, [tab.id]: e.target.value }))} style={{ ...inp, width: 120 }} />
                        </div>
                      )}
                      <label style={{ fontSize: 12, color: "#5b6472" }}>{t("salesperson")}
                        <select value={salesperson[tab.id] || ""} onChange={(e) => setSalesperson((m) => ({ ...m, [tab.id]: e.target.value }))}
                          style={{ ...inp, width: "100%", marginTop: 3 }}>
                          <option value="">{t("salesperson_auto")}</option>
                          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </label>
                      <div style={{ display: "flex", gap: 8 }}>
                        {methodSel(tab)}
                        <button className="btn" style={{ flex: 1 }} disabled={busy === tab.id} onClick={() => recordBill(tab)}>
                          {busy === tab.id ? "…" : `✓ ${t("record_bill")}`}
                        </button>
                      </div>
                      <button className="btn secondary" onClick={() => toggleSplit(tab)}>
                        🧾 {t("split_bill")}
                      </button>
                      {splitOpen[tab.id] && (() => {
                        const selIds = splitSel[tab.id] || [];
                        const selSum = items
                          .filter((it, idx) => selIds.includes(idx))
                          .reduce((a, it) => a + (it.line_total || 0), 0);
                        return (
                          <div style={{ padding: 10, background: "#f2f9f8", borderRadius: 8 }}>
                            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>{t("select_to_split")}</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              {items.map((it, i) => (
                                it.settled ? (
                                  <div key={it.id ?? i} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 14, padding: "3px 0", color: "#8a9a9a", textDecoration: "line-through" }}>
                                    <span>{it.quantity}× {it.name} <span style={{ fontSize: 11, textDecoration: "none", background: "#dbe7e7", color: "#3a5555", borderRadius: 6, padding: "1px 6px" }}>{t("paid")}</span></span>
                                    <span>{money(it.line_total)}</span>
                                  </div>
                                ) : (
                                  <label key={it.id ?? i} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 14, padding: "3px 0", cursor: "pointer", alignItems: "center" }}>
                                    <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                      <input type="checkbox" checked={selIds.includes(i)} onChange={() => toggleSplitItem(tab.id, i)} />
                                      {it.quantity}× {it.name}
                                    </span>
                                    <span>{money(it.line_total)}</span>
                                  </label>
                                )
                              ))}
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 8, borderTop: "1px solid #dbe7e7", paddingTop: 8 }}>
                              <span style={{ fontSize: 13 }}>{t("selected")}: <b>{money(selSum)}</b></span>
                              <button className="btn" disabled={busy === tab.id || selIds.length === 0} onClick={() => paySelected(tab)}>
                                {busy === tab.id ? "…" : `✓ ${t("pay_selected")}`}
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                      {allowGroupCharge && (
                      <button className="btn secondary" onClick={() => toggleTransfer(tab)}>
                        ➡ {t("transfer")}
                      </button>
                      )}
                      {allowGroupCharge && transferOpen[tab.id] && (() => {
                        const selIds = transferSel[tab.id] || [];
                        const selSum = items
                          .filter((it, idx) => selIds.includes(idx))
                          .reduce((a, it) => a + (it.line_total || 0), 0);
                        const targets = (tables || []).filter((tb) => tb.id !== tab.table_id);
                        const target = transferTarget[tab.id] || "";
                        return (
                          <div style={{ padding: 10, background: "#f2f9f8", borderRadius: 8 }}>
                            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>{t("select_to_split")}</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              {items.map((it, i) => (
                                it.settled ? (
                                  <div key={it.id ?? i} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 14, padding: "3px 0", color: "#8a9a9a", textDecoration: "line-through" }}>
                                    <span>{it.quantity}× {it.name} <span style={{ fontSize: 11, textDecoration: "none", background: "#dbe7e7", color: "#3a5555", borderRadius: 6, padding: "1px 6px" }}>{t("paid")}</span></span>
                                    <span>{money(it.line_total)}</span>
                                  </div>
                                ) : (
                                  <label key={it.id ?? i} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 14, padding: "3px 0", cursor: "pointer", alignItems: "center" }}>
                                    <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                      <input type="checkbox" checked={selIds.includes(i)} onChange={() => toggleTransferItem(tab.id, i)} />
                                      {it.quantity}× {it.name}
                                    </span>
                                    <span>{money(it.line_total)}</span>
                                  </label>
                                )
                              ))}
                            </div>
                            <div style={{ marginTop: 8, borderTop: "1px solid #dbe7e7", paddingTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
                                <span className="muted">{t("transfer_to")}</span>
                                <select value={target} onChange={(e) => setTransferTarget((m) => ({ ...m, [tab.id]: e.target.value }))}
                                  style={{ ...inp, flex: 1 }}>
                                  <option value="">{t("choose_table")}</option>
                                  {targets.map((tb) => (
                                    <option key={tb.id} value={tb.id}>{t("table")} {tb.table_number}</option>
                                  ))}
                                </select>
                              </label>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 13 }}>{t("selected")}: <b>{money(selSum)}</b></span>
                                <button className="btn" disabled={busy === tab.id || selIds.length === 0 || !target} onClick={() => doTransfer(tab)}>
                                  {busy === tab.id ? "…" : `➡ ${t("transfer_selected")}`}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {fdOn && (
                        <>
                          <button className="btn secondary" onClick={() => openRoomPick(tab)}>🏨 {t("charge_to_room")}</button>
                          {roomPick[tab.id] && (
                            <div style={{ padding: 10, background: "#f2f9f8", borderRadius: 8 }}>
                              <input placeholder={t("search_room_or_name")} value={gQuery[tab.id] || ""}
                                onChange={(e) => setGQuery((m) => ({ ...m, [tab.id]: e.target.value }))} style={{ ...inp, width: "100%" }} />
                              <div style={{ maxHeight: 180, overflowY: "auto", marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                                {inhouse == null ? (
                                  <span className="muted" style={{ fontSize: 13 }}>…</span>
                                ) : filterGuests(gQuery[tab.id]).length === 0 ? (
                                  <span className="muted" style={{ fontSize: 13 }}>{t("no_inhouse")}</span>
                                ) : filterGuests(gQuery[tab.id]).map((g) => (
                                  <button key={g.id} className="btn secondary" style={{ textAlign: "left" }} disabled={busy === tab.id} onClick={() => doChargeRoom(tab, g)}>
                                    {g.room_name ? <b>{g.room_name}</b> : null}{g.room_name ? " · " : ""}{g.guest_name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {allowGroupCharge && <button className="btn secondary" onClick={() => openGroupPick(tab)}>👥 {t("charge_to_group")}</button>}
                          {allowGroupCharge && groupPick[tab.id] && (
                            <div style={{ padding: 10, background: "#f2f9f8", borderRadius: 8 }}>
                              <input placeholder={t("search_group")} value={bQuery[tab.id] || ""}
                                onChange={(e) => setBQuery((m) => ({ ...m, [tab.id]: e.target.value }))} style={{ ...inp, width: "100%" }} />
                              <div style={{ maxHeight: 180, overflowY: "auto", marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                                {blocks == null ? (
                                  <span className="muted" style={{ fontSize: 13 }}>…</span>
                                ) : filterBlocks(bQuery[tab.id]).length === 0 ? (
                                  <span className="muted" style={{ fontSize: 13 }}>{t("no_blocks")}</span>
                                ) : filterBlocks(bQuery[tab.id]).map((b) => (
                                  <button key={b.id} className="btn secondary" style={{ textAlign: "left" }} disabled={busy === tab.id} onClick={() => doChargeGroup(tab, b)}>
                                    {b.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )
      ) : (
        closed.length === 0 ? (
          <div className="card" style={{ marginTop: 12, padding: 24, textAlign: "center" }}><p className="muted" style={{ margin: 0 }}>{t("no_billed")}</p></div>
        ) : (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {closed.map((tab) => (
              <div key={tab.id} className="card" style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
                  <b>{t("table")} {tab.table_number ?? "—"}</b>
                  <span className="muted" style={{ fontSize: 12 }}>{tab.payment_method || ""} {tab.payment_reference ? "· #" + tab.payment_reference : ""} · {(tab.paid_at || "").slice(11, 16)}</span>
                </div>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <b>{money(tab.total)}</b>
                  <button className="btn secondary" onClick={() => smartPrint(tab)}>🖨</button>
                  <button className="btn secondary" disabled={busy === tab.id} onClick={() => doReopen(tab)}>{t("reopen")}</button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <p className="muted" style={{ marginTop: 16, fontSize: 12 }}>{t("note_fiscal")}</p>
    </div>
  );
}

function escapeHtml(s) {
  return String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
}

// Gregorian -> Ethiopian calendar (JDN based). Returns {y,m,d}.
function gregToEth(gy, gm, gd) {
  const a = Math.floor((14 - gm) / 12), y = gy + 4800 - a, m = gm + 12 * a - 3;
  const jdn = gd + Math.floor((153 * m + 2) / 5) + 365 * y + Math.floor(y / 4)
    - Math.floor(y / 100) + Math.floor(y / 400) - 32045;
  const epoch = 1723856, r = ((jdn - epoch) % 1461 + 1461) % 1461;
  const n = (r % 365) + 365 * Math.floor(r / 1460);
  const ey = 4 * Math.floor((jdn - epoch) / 1461) + Math.floor(r / 365) - Math.floor(r / 1460);
  return { y: ey, m: Math.floor(n / 30) + 1, d: (n % 30) + 1 };
}

// English number-to-words for the "total in words" line on the fiscal invoice.
const _ONES = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten",
  "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
const _TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
function _u999(n) {
  let w = "";
  if (n >= 100) { w += _ONES[Math.floor(n / 100)] + " hundred"; n %= 100; if (n) w += " "; }
  if (n >= 20) { w += _TENS[Math.floor(n / 10)]; if (n % 10) w += "-" + _ONES[n % 10]; }
  else if (n) w += _ONES[n];
  return w;
}
function numToWords(v) {
  let n = Math.round(v || 0);
  if (n === 0) return "Zero";
  const parts = [];
  [[1000000, "million"], [1000, "thousand"], [1, ""]].forEach(([div, name]) => {
    if (n >= div) { parts.push(_u999(Math.floor(n / div)) + (name ? " " + name : "")); n %= div; }
  });
  const s = parts.join(", ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Build the ODRA fiscal-style A4 invoice (MoR/DROGA bilingual layout).
// fiscal = {irn, rrn, qr_image, provider, system_number, invoice_no} or null (proforma).
function fiscalBillHtml(tab, { user, fiscal, cur, money, proformaLabel, venueLogo }) {
  const esc = escapeHtml;
  const items = (tab.rounds || []).flatMap((r) => r.items || []);
  const vatPct = tab.vat_pct || 15;
  const L = (am, en) => `<span class="am">${am}</span> · <span class="en">${en}</span>`;
  const kv = (am, en, val) => `<div class="kv"><div class="kl">${L(am, en)}</div><div class="vv">${val}</div></div>`;
  const rows = items.map((it, i) => {
    const qty = it.quantity || 1;
    const unit = it.unit_price != null ? it.unit_price : (it.line_total || 0) / qty;
    const tc = tab.vat > 0 ? `VAT${vatPct}` : "VATEX";
    return `<tr><td>${i + 1}</td><td>${esc(it.item_code || "—")}</td><td class="l">${esc(it.name)}</td>`
      + `<td>goods</td><td>PCS</td><td class="r">${qty}</td><td class="r">${money(unit)}</td>`
      + `<td>${tc}</td><td class="r b">${money(it.line_total)}</td></tr>`;
  }).join("");

  const now = new Date();
  const eth = gregToEth(now.getFullYear(), now.getMonth() + 1, now.getDate());
  const ethS = `${String(eth.d).padStart(2, "0")}-${String(eth.m).padStart(2, "0")}-${eth.y} ዓ/ም`;
  const gregS = `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()} G.C`;
  const timeS = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  const venue = (user || {}).name || "ODRA Venue";
  const sTin = (user || {}).tin || (user || {}).vat_number || "—";
  const sSys = (fiscal && fiscal.system_number) || (user || {}).system_number || "—";
  const buyerTin = tab.buyer_tin || null;
  const b2b = !!buyerTin;
  const buyer = tab.buyer_name || (b2b ? "—" : "Walk-in customer");
  const docNo = tab.id != null ? tab.id : "";
  const invNo = (fiscal && fiscal.invoice_no) || `OD-${String(now.getFullYear()).slice(2)}-${String(docNo).padStart(7, "0")}`;

  const net = tab.subtotal || 0, svc = tab.service_charge || 0, vat = tab.vat || 0, gross = tab.total || 0;
  const totals = [
    ["ድምር", "Subtotal (ETB)", money(net), ""],
    ...(tab.discount > 0 ? [["የቅናሽ መጠን", "Discount", "−" + money(tab.discount), ""]] : []),
    ...(svc > 0 ? [["አገልግሎት ክፍያ (" + (tab.service_charge_pct || 10) + "%)", "Service Charge", money(svc), ""]] : []),
    ["ታክስ የሚከፈልበት ድምር", "Taxable Total", money(net), ""],
    ["ተ.እ.ታ (" + vatPct + "%)", "VAT", money(vat), ""],
    ["ጠቅላላ ዋጋ ከታክስ ጋር", "Total including Tax", money(gross), "bar"],
    ["የተጣራ ክፍያ", "Net Payable", money(gross), "g"],
  ].map(([am, en, v, hi]) => `<tr class="${hi === "bar" ? "bar" : (hi === "g" ? "g" : "")}"><td>${L(am, en)}</td><td class="r">${v}</td></tr>`).join("");

  const frm = kv("ከተማ", "City/Town", esc((user || {}).city || "—")) + kv("ዞን / ክ/ከተማ", "Zone/Sub city", esc((user || {}).sub_city || "—")) + kv("ወረዳ", "Woreda", esc((user || {}).woreda || "—")) +
    kv("ስልክ ቁጥር", "Phone No", esc((user || {}).phone || "—")) + kv("የግብር ከፋይ መለያ", "TIN", `<b>${esc(sTin)}</b>`) + kv("ተ.እ.ታ ቁጥር", "VAT Reg. No", esc((user || {}).vat_number || "—"));
  const to = kv("ስልክ ቁጥር", "Phone No", "—") + kv("የግብር ከፋይ መለያ", "TIN", `<b>${esc(buyerTin || "—")}</b>`) + kv("መታወቂያ", "ID Type / No", "—");

  const fiscalised = !!(fiscal && fiscal.irn);
  const title = fiscalised ? L("የሽያጭ ደረሰኝ / ተ.እ.ታ", "Sales Invoice / VAT") : L("ጊዜያዊ ደረሰኝ", "Proforma Invoice");
  const qrBlock = fiscalised && fiscal.qr_image ? `<div class="qr"><img src="${fiscal.qr_image}"/></div>` : `<div class="qr qph">${L("ኪው አር", "QR on fiscalization")}</div>`;
  const irnBar = fiscalised
    ? `<div class="irnbar ok"><b>IRN</b> <span class="mono">${esc(fiscal.irn)}</span> &nbsp;|&nbsp; <b>System No</b> ${esc(sSys)} &nbsp;|&nbsp; <b>Reg.</b> ${gregS} ${timeS}${fiscal.provider === "mock" ? " &nbsp;·&nbsp; (dry-run)" : ""}</div>`
    : `<div class="irnbar warn">${esc(proformaLabel)} — ${L("ክፍያ ሲፈጸም ኦፊሴላዊ ደረሰኝ ይመዘገባል", "a fiscal IRN is registered when the bill is settled")}</div>`;
  const words = numToWords(gross) + " birr";
  const origin = (typeof window !== "undefined" && window.location) ? window.location.origin : "";
  // Hotel's own logo on the invoice; fall back to the ODRA mark if none set.
  const logoSrc = venueLogo || `${origin}/horizontal.png`;

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(venue)} — ${fiscalised ? "Invoice " + esc(invNo) : "Proforma"}</title><style>
 @page { size: A4; margin: 10mm; }
 * { box-sizing: border-box; }
 body { font-family:'Nyala','Abyssinica SIL','Noto Sans Ethiopic',Arial,sans-serif; color:#111; font-size:10px; margin:0; }
 .am { font-family:'Nyala','Abyssinica SIL','Noto Sans Ethiopic',sans-serif; font-size:8.5px; color:#444; }
 .en { font-size:9px; } .mono { font-family:'Courier New',monospace; font-size:8px; word-break:break-all; } .b{font-weight:700}
 .inv { border:1px solid #333; position:relative; }
 .hdr { display:flex; align-items:stretch; border-bottom:2px solid #0E7C7B; }
 .hdr > div { padding:6px 8px; border-right:1px solid #ccc; } .hdr > div:last-child { border-right:none; }
 .logo { display:flex; align-items:center; } .logo img { height:46px; }
 .co { min-width:160px; } .co-nm { font-size:15px; font-weight:800; color:#0E7C7B; } .co-sm { font-size:8.5px; color:#444; }
 .title { flex:1; text-align:center; display:flex; flex-direction:column; justify-content:center; }
 .title .am { font-size:11px; } .title .en { font-size:12px; font-weight:700; color:#0E7C7B; }
 .badge { align-self:center; margin-top:4px; background:#d6efee; color:#0E7C7B; font-size:9px; font-weight:700; padding:1px 10px; border-radius:2px; }
 .dm { min-width:150px; font-size:9px; } .dl { color:#555; font-size:8px; }
 .dbig { font-size:24px; font-weight:800; text-align:right; color:#0E7C7B; line-height:1; margin-bottom:3px; }
 .dr { display:flex; justify-content:space-between; gap:6px; margin:1px 0; } .dr span { color:#555; } .dr b { text-align:right; }
 .qr { display:flex; align-items:center; justify-content:center; width:96px; } .qr img { width:90px; height:90px; }
 .qr.qph { font-size:8px; color:#999; text-align:center; border:1px dashed #ccc; padding:6px; }
 .irnbar { padding:4px 8px; font-size:8.5px; border-bottom:1px solid #ccc; }
 .irnbar.ok { background:#f4f6f8; border-top:2px solid #E0A500; }
 .irnbar.warn { background:#fff6e5; color:#9a6b00; border-top:2px solid #E0A500; text-align:center; font-weight:700; }
 .parties { display:flex; } .party { flex:1; padding:5px 8px; border-right:1px solid #ccc; } .party:last-child { border-right:none; }
 .ph { font-size:11px; color:#0E7C7B; margin-bottom:4px; border-bottom:1px solid #eee; padding-bottom:2px; }
 .grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:3px 8px; }
 .kv .kl { color:#555; font-size:8px; line-height:1.1; } .kv .vv { font-size:9.5px; }
 .midrow { display:grid; grid-template-columns:repeat(4,1fr); border-top:1px solid #333; border-bottom:1px solid #333; }
 .midrow .kv { padding:4px 8px; border-right:1px solid #ccc; } .midrow .kv:last-child { border-right:none; }
 table.items { width:100%; border-collapse:collapse; }
 table.items th, table.items td { border:1px solid #ccc; padding:3px 5px; text-align:center; font-size:9.5px; }
 table.items th { background:#eef6f6; } .l{text-align:left!important} .r{text-align:right!important} table.items td.b{font-weight:700}
 .bottom { display:flex; border-top:1px solid #333; }
 .left { flex:1; padding:6px 8px; border-right:1px solid #333; }
 .words { margin-bottom:6px; } .words .wl { color:#555; font-size:8px; } .words b { font-size:11px; }
 .pay .kv { margin:3px 0; } .sign { margin-top:10px; font-size:9px; color:#333; } .sign .sl { border-bottom:1px solid #999; width:180px; margin-top:16px; }
 table.totals { width:40%; border-collapse:collapse; }
 table.totals td { padding:3px 8px; border-bottom:1px solid #eee; font-size:9.5px; } table.totals td:last-child { text-align:right; width:38%; }
 table.totals tr.g td { font-weight:700; }
 table.totals tr.bar td { background:#0E7C7B; color:#fff; font-weight:800; } table.totals tr.bar td .am, table.totals tr.bar td .en { color:#fff; }
 .regfoot { border-top:1px solid #333; padding:4px 8px; font-size:8px; color:#555; }
</style></head><body>
 <section class="inv">
  <div class="hdr">
   <div class="logo"><img src="${logoSrc}" onerror="this.style.display='none'"/></div>
   <div class="co"><div class="co-nm">${esc(venue)}</div><div class="co-sm">${esc((user || {}).phone || "")}</div><div class="co-sm" style="color:#0E7C7B">Powered by ODRA</div></div>
   <div class="title">${title}<div class="badge">${b2b ? "B2B" : "B2C"}</div></div>
   <div class="dm">
     <div class="dl">${L("የደረሰኝ ቁጥር", "Document No")}</div><div class="dbig">${esc(String(docNo))}</div>
     <div class="dr"><span>${L("ደረሰኝ ቁ.", "Invoice No")}</span><b>${esc(invNo)}</b></div>
     <div class="dr"><span>${L("ቀን", "Date")}</span><b>${ethS}<br>${gregS}</b></div>
     <div class="dr"><span>${L("ሰአት", "Time")}</span><b>${timeS}</b></div>
   </div>
   ${qrBlock}
  </div>
  ${irnBar}
  <div class="parties">
   <div class="party"><div class="ph">${L("ከ", "FROM")} &nbsp; <b>${esc(venue)}</b></div><div class="grid">${frm}</div></div>
   <div class="party"><div class="ph">${L("ለ", "TO")} &nbsp; <b>${esc(buyer)}</b></div><div class="grid">${to}</div></div>
  </div>
  <div class="midrow">
   ${kv("ጠረጴዛ / ትዕዛዝ", "Table / Order", esc((tab.table_number != null ? "T-" + tab.table_number : "—") + " · #" + docNo))}${kv("የክፍያ ጊዜ", "Payment Term", "Immediate")}${kv("ሻጭ", "Sales Person", esc(tab.waiter_name || "—"))}${kv("ገንዘብ ተቀባይ", "Cashier", "ODRA POS")}
  </div>
  <table class="items">
   <tr><th>${L("ተ/ቁ", "No.")}</th><th>${L("ኮድ", "Code")}</th><th class="l">${L("የዕቃው / አገልግሎት", "Description")}</th><th>${L("ምድብ", "Nature")}</th><th>${L("መለኪያ", "UoM")}</th><th>${L("ብዛት", "Qty")}</th><th>${L("የአንዱ ዋጋ", "Unit Price")}</th><th>${L("ታክስ ኮድ", "Tax Code")}</th><th>${L("ጠቅላላ ዋጋ", "Total")}</th></tr>
   ${rows}
  </table>
  <div class="bottom">
   <div class="left">
     <div class="words"><div class="wl">${L("ጠቅላላ ዋጋ ከታክስ ጋር (በፊደል)", "Total including Tax (in words)")}</div><b>${words}</b></div>
     <div class="pay">${kv("የክፍያ ሁኔታ", "Mode of Payment", "<b>Telebirr / QR</b>")}${kv("ያዘጋጀው", "Prepared By", "ODRA POS")}</div>
     <div class="sign">${L("የተቀባይ ስምና ፊርማ", "Receiver Name & Signature")}<div class="sl"></div></div>
   </div>
   <table class="totals">${totals}</table>
  </div>
  <div class="regfoot">${fiscalised
      ? `Registered with the Ministry of Revenue EIMS on ${gregS} ${timeS}. Scan the QR code to verify.${fiscal.rrn ? " · RRN " + esc(fiscal.rrn) : ""} · Fiscalized by KODIS e-Invoice`
      : "Proforma — not a fiscal receipt. Settle the bill to register the fiscal invoice (IRN) with the Ministry of Revenue."} · odra.app</div>
 </section>
</body></html>`;
}
